<script>
import detectIndent from 'detect-indent';
import CodeMirror from 'codemirror/lib/codemirror.js';
import 'codemirror/mode/htmlmixed/htmlmixed';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/mode/php/php';
import 'codemirror/mode/css/css';
import 'codemirror/addon/display/fullscreen';
import 'codemirror/addon/display/rulers';
import 'codemirror/addon/edit/matchbrackets';
import 'codemirror/addon/edit/matchtags';
import 'codemirror/addon/mode/overlay';
import 'codemirror/lib/codemirror.css';

export default {
  name: 'CodeEditor',
  props: {
    allowFullScreen: {
      type: Boolean,
      default: true
    },
    height: {
      type: String,
      default: '200px' // ~ 10 lines
    },
    lineNumbers: {
      type: Boolean,
      default: true
    },
    lineWrapping: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      validator(value) {
        return ['none', 'css', 'html', 'javascript', 'php'].includes(value);
      }
    },
    value: String
  },
  data() {
    return {
      dragging: false,
      editor: null,
      fullScreen: false,
      hasFocus: false
    };
  },
  watch: {
    fullScreen() {
      if (this.allowFullScreen && this.fullScreen) {
        this.setOption('fullScreen', true);
      } else {
        this.setOption('fullScreen', false);
      }
    },
    lineNumbers() {
      this.setOption('lineNumbers', this.lineNumbers);
    },
    lineWrapping() {
      this.setOption('lineWrapping', this.lineWrapping);
    },
    mode() {
      if (this.mode === 'none') {
        this.setOption('mode', null);
      } else {
        this.setOption('mode', this.mode === 'html' ? 'html-with-cms-classes' : this.mode);
      }
    },
    value() {
      this.setValue(this.value);
    }
  },
  mounted() {
    // Initialize CodeMirror
    const container = this.$refs.container;

    // Define a custom overlay mode to highlight special CMS classes
    //
    // NOTE: this is a dumb match that could be improved to ensure the keywords are actually inside
    // a class attribute.
    CodeMirror.defineMode('html-with-cms-classes', config => {
      const mode = {
        token: function(stream) {
          // Special comments
          if (stream.match(/<!--\/?cms-ignore-->/i)) return 'cms-special-comment';

          // CMS classes
          if (stream.match(/cms-editable/i)) return 'cms-editable';
          if (stream.match(/cms-repeatable/i)) return 'cms-repeatable';
          if (stream.match(/cms-include/i)) return 'cms-include';
          if (stream.match(/cms-lock/i)) return 'cms-lock';
          if (stream.match(/cms-unlock/i)) return 'cms-unlock';

          // CMS elements
          if (stream.match(/cms-code/i)) return 'cms-code';
          if (stream.match(/cms-embed/i)) return 'cms-embed';
          if (stream.match(/cms-gallery/i)) return 'cms-gallery';

          stream.next();
          return null;
        }
      };

      return CodeMirror.overlayMode(CodeMirror.getMode(config, 'htmlmixed'), mode);
    });

    // Initialize CodeMirror
    this.editor = CodeMirror.fromTextArea(this.$refs.textarea, {
      lineNumbers: this.lineNumbers,
      lineWrapping: this.lineWrapping,
      mode: this.mode === 'html' ? 'html-with-cms-classes' : this.mode,
      matchBrackets: true,
      matchTags: { bothTags: true },
      rulers: [
        {
          column: 120,
          color: '#f5f5f5',
          lineStyle: 'solid',
          width: '1px'
        }
      ],
      viewportMargin: Infinity,
      extraKeys: {
        Tab: cm => cm.execCommand('indentMore'),
        'Shift-Tab': cm => cm.execCommand('indentLess')
      }
    });
    this.editor.on('change', this.handleChange);
    this.editor.setValue(this.value);
    this.autoDetectIndentation();

    // Watch for drops on the editor
    this.editor.on('dragenter', (cm, event) => this.handleDragEnter(event));
    this.editor.on('dragleave', (cm, event) => this.handleDragLeave(event));
    this.editor.on('dragover', (cm, event) => this.handleDragOver(event));
    this.editor.on('drop', (cm, event) => this.handleDrop(event));
    this.editor.on('focus', () => (this.hasFocus = true));
    this.editor.on('blur', () => (this.hasFocus = false));

    // Redraw the editor as soon as it's displayed
    this.$nextTick(() => this.editor.refresh());

    // Watch for drops on the container (in case the CodeMirror element isn't full height)
    container.addEventListener('dragenter', event => this.handleDragEnter(event));
    container.addEventListener('dragleave', event => this.handleDragLeave(event));
    container.addEventListener('dragover', event => this.handleDragOver(event));
    container.addEventListener('drop', event => this.handleDrop(event));

    // Clicks on the container should bring focus to the editor
    container.addEventListener('click', event => {
      if (event.target.classList.contains('CodeMirror')) {
        this.editor.focus();
      }
    });

    // Escape should close full screen mode
    document.addEventListener('keydown', this.handleEscape);
  },
  beforeDestroy() {
    // Destroy the CodeMirror instance
    if (this.editor) {
      this.editor.toTextArea();
    }

    // Destroy listeners
    document.removeEventListener('keydown', this.handleEscape);
  },
  methods: {
    // Auto-detects and sets the editor's indentation based on its content
    autoDetectIndentation() {
      if (this.editor) {
        const indent = detectIndent(this.value);

        if (indent.type === 'tab') {
          this.setOption('indentUnit', 4);
          this.setOption('indentWithTabs', true);
        } else {
          this.setOption('indentUnit', indent.amount || 2);
          this.setOption('indentWithTabs', false);
        }
      }
    },

    // Clears the editor's undo history
    clearHistory() {
      if (this.editor) {
        this.editor.clearHistory();
      }
    },

    // Focus the editor
    focus() {
      if (this.editor) {
        this.editor.focus();
      }
    },

    // Emit the editor's value when its content changes
    handleChange() {
      if (this.editor) {
        // Only emit if the value has actually changed
        if (this.value !== this.editor.getValue()) {
          this.$emit('input', this.editor.getValue());
        }
      }
    },

    // Exits full screen mode when escape is pressed
    handleEscape(event) {
      if (event.key === 'Escape' && this.fullScreen) {
        event.preventDefault();
        this.fullScreen = false;
      }
    },

    // Handle the dragenter event
    handleDragEnter(event) {
      event.preventDefault();
    },

    // Handle the dragleave event
    handleDragLeave(event) {
      event.preventDefault();
      this.dragging = false;
    },

    // Handle the dragover event
    handleDragOver(event) {
      event.preventDefault();
      this.dragging = true;
    },

    // Handle dropped files
    handleDrop(event) {
      const file = event.dataTransfer.files[0];
      const reader = new FileReader();

      event.preventDefault();
      this.dragging = false;

      // No file was dropped
      if (!file) {
        return;
      }

      // Allow text mime types, but also allow empty ones (unknown extensions are empty)
      if (file.type === '' || file.type.match(/^text\//)) {
        reader.onload = event => {
          const result = event.target.result.replace(/\r/g, '\n'); // fix line breaks
          this.setValue(result);
        };
        reader.readAsText(file);
      }
    },

    // Sets an editor option
    setOption(name, value) {
      if (this.editor) {
        this.editor.setOption(name, value);
      }
    },

    // Sets the editor's value
    setValue(value) {
      if (this.editor && value !== this.editor.getValue()) {
        const cursor = this.editor.getCursor();
        this.editor.setValue(value);
        this.editor.setCursor(cursor);
        this.autoDetectIndentation();
      }
    }
  }
};
</script>

<template>
  <div
    ref="container"
    :class="{
      'code-editor': true,
      'has-focus': hasFocus,
      'is-dragging': dragging,
      'is-full-screen': fullScreen
    }"
    :style="`height: ${height}`"
  >
    <textarea ref="textarea" class="code-editor-textarea" />
    <span v-if="allowFullScreen" :title="$t('Toggle Full Screen Mode')" class="full-screen-toggle" @click="fullScreen = !fullScreen">
      <i :class="fullScreen ? 'el-icon-fa-compress' : 'el-icon-fa-expand'" />
    </span>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

$--code-editor-bg: $--color-white;
$--code-editor-full-screen-z-index: 9000;
$--code-editor-focus: $--color-primary;
$--code-editor-selection-bg: $--color-primary-light-8;

.code-editor {
  position: relative;
  background-color: $--code-editor-bg;
  border: solid 1px $--input-border-color;
  border-radius: 0.25rem;
  padding: 0.125rem 0;
  margin-bottom: 0.5rem;
  transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

  /deep/ .CodeMirror {
    width: 100%;
    height: 100%;
    font-family: $--font-family-monospace;
    font-size: 13px;
    line-height: 1.5;
    background-color: $--code-editor-bg;

    pre {
      padding-left: 0.5rem;
    }

    .CodeMirror-selected {
      background: $--code-editor-selection-bg;
    }

    .CodeMirror-line::selection,
    .CodeMirror-line > span::selection,
    .CodeMirror-line > span > span::selection {
      background: $--code-editor-selection-bg;
    }

    .CodeMirror-gutters {
      background-color: $--code-editor-bg;
      border-right: 1px $--code-editor-bg;
    }

    .CodeMirror-guttermarker {
      color: $--color-white;
    }

    .CodeMirror-guttermarker-subtle {
      color: $--color-gray-40;
    }

    .CodeMirror-linenumber {
      color: $--color-gray-30;
      transform: scale(0.8);
    }

    .CodeMirror-cursor {
      border-left: 2px solid $--color-primary;
    }

    .cm-comment {
      color: $--color-gray-40;
    }
    .cm-atom {
      color: #9e76e6;
    }
    .cm-meta {
      color: $--color-gray-40;
    }
    .cm-number {
      color: #ed5c65;
    }
    .cm-property {
      color: #ed5c65;
    }
    .cm-attribute {
      color: #dc7f13;
    }
    .cm-keyword {
      color: #a151d2;
    }
    .cm-string {
      color: #269061;
    }
    .cm-string-2 {
      color: #ed5c65;
    } // e.g. regex
    .cm-variable {
      color: #2795ee;
    }
    .cm-variable-2 {
      color: #2795ee;
    }
    .cm-variable-3 {
      color: #269061;
    }
    .cm-def {
      color: #f18c16;
    }
    .cm-bracket {
      color: #39464e;
    }
    .cm-tag {
      color: darken($--color-primary, 5%);
    }
    .cm-header {
      color: blue;
    }
    .cm-link {
      text-decoration: underline;
    }
    .cm-error {
      background: #ca2d2e !important;
      color: $--color-white !important;
    }

    // Special comments
    .cm-cms-special-comment {
      color: $--color-gray-40;
      font-weight: bold;
    }

    // CMS classes
    .cm-cms-editable,
    .cm-cms-repeatable,
    .cm-cms-include,
    .cm-cms-lock,
    .cm-cms-unlock {
      color: darken($--color-primary, 5%);
      font-weight: bold;
    }

    // CMS elements
    .cm-cms-code,
    .cm-cms-embed,
    .cm-cms-gallery {
      color: darken($--color-primary, 5%);
      font-weight: bold;
    }

    // Addons
    &.CodeMirror-fullscreen {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: $--code-editor-full-screen-z-index;
      height: auto;
      border-radius: 0;
    }

    .CodeMirror-activeline-background {
      background: $--code-editor-selection-bg;
    }

    .CodeMirror-matchingbracket {
      color: inherit;
      background: lighten($--code-editor-selection-bg, 5%);
    }

    .CodeMirror-matchingtag {
      background: lighten($--code-editor-selection-bg, 5%);
    }
  }

  &.has-focus {
    border-color: $--code-editor-focus;
  }

  &.is-dragging {
    background-color: $--code-editor-selection-bg;
    border-color: $--code-editor-focus;

    /deep/ .CodeMirror,
    /deep/ .CodeMirror-gutter {
      background-color: $--code-editor-selection-bg;
    }
  }

  // Full screen toggle
  .full-screen-toggle {
    position: absolute;
    z-index: 3; // CodeMirror uses a z-index of 2 for its lines
    top: 0.5rem;
    right: 0.5rem;
    font-size: 1.1rem;
    line-height: 1;
    color: $--color-gray-40;
    padding: 0.25rem;
    cursor: pointer;

    &:hover {
      color: $--color-gray-60;
    }
  }

  &.is-full-screen .full-screen-toggle {
    position: fixed;
    z-index: $--code-editor-full-screen-z-index;
  }
}

// Error state
.el-form-item.is-error .code-editor /deep/ {
  .CodeMirror {
    border-color: $--color-danger;
  }
}
</style>
